/**
 * Hero lazy slides output `<source data-src="...">` (see _hero-slide.liquid) so the
 * browser does not fetch until we promote to `src` and call `load()`.
 */
function hydrateLazyVideoSources(video) {
    if (!video) return;

    let changed = false;

    const videoDataSrc = video.getAttribute('data-src');
    if (videoDataSrc) {
        video.src = videoDataSrc;
        video.removeAttribute('data-src');
        changed = true;
    }

    for (const source of video.querySelectorAll('source[data-src]')) {
        const url = source.getAttribute('data-src');
        if (url) {
            source.src = url;
            source.removeAttribute('data-src');
            changed = true;
        }
    }

    if (changed) {
        video.load();
    }
}

class VideoToggle extends HTMLElement {
    constructor() {
        super();
        this._video = null;
        this._pauseButton = null;
        this._playButton = null;
        this._autoplayHoverZone = null;
        this._autoplayHoverVideo = null;

        this._handlePauseClick = this._handlePauseClick.bind(this);
        this._handlePlayClick = this._handlePlayClick.bind(this);
        this._syncUiState = this._syncUiState.bind(this);
        this._handleAutoplayPointerEnter = this._handleAutoplayPointerEnter.bind(this);
        this._handleAutoplayPointerOut = this._handleAutoplayPointerOut.bind(this);
    }

    connectedCallback() {
        const videoWrapper = this.closest('video-wrapper');
        if (!videoWrapper) return;

        this._video = videoWrapper.querySelector('video');
        if (!this._video) return;

        hydrateLazyVideoSources(this._video);

        const buttons = this.querySelectorAll('button');
        this._pauseButton = buttons[0] || null;
        this._playButton = buttons[1] || null;

        if (!this._pauseButton || !this._playButton) return;

        this._pauseButton.setAttribute('type', 'button');
        this._pauseButton.setAttribute('aria-controls', this._ensureVideoId());
        this._pauseButton.setAttribute('aria-label', 'Pause video');

        this._playButton.setAttribute('type', 'button');
        this._playButton.setAttribute('aria-controls', this._ensureVideoId());
        this._playButton.setAttribute('aria-label', 'Play video');

        this._pauseButton.addEventListener('click', this._handlePauseClick);
        this._playButton.addEventListener('click', this._handlePlayClick);

        this._video.addEventListener('play', this._syncUiState);
        this._video.addEventListener('pause', this._syncUiState);
        this._video.addEventListener('ended', this._syncUiState);

        this._attachAutoplayHover();

        this._syncUiState();
    }

    disconnectedCallback() {
        if (this._pauseButton) {
            this._pauseButton.removeEventListener('click', this._handlePauseClick);
        }
        if (this._playButton) {
            this._playButton.removeEventListener('click', this._handlePlayClick);
        }
        if (this._video) {
            this._video.removeEventListener('play', this._syncUiState);
            this._video.removeEventListener('pause', this._syncUiState);
            this._video.removeEventListener('ended', this._syncUiState);
        }
        this._detachAutoplayHover();
    }

    _handlePauseClick() {
        if (!this._video) return;
        this._video.pause();
    }

    _handlePlayClick() {
        if (!this._video) return;
        this._video.play().catch(() => {
            // Ignore autoplay/gesture-related rejections.
        });
    }

    /**
     * Play on hover, pause on leave. Listens on both the wrapper and this control and
     * uses `relatedTarget` to avoid pausing when moving between video and buttons.
     * Disabled for touch-primary UIs and when `prefers-reduced-motion: reduce`.
     * Enable by setting the `autoplay-hover` attribute on `<video-toggle>`.
     */
    _attachAutoplayHover() {
        if (!this.hasAttribute('autoplay-hover') || !this._video) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        this._autoplayHoverZone = this._video.closest('video-wrapper');
        if (this._autoplayHoverZone) {
            this._autoplayHoverZone.addEventListener('pointerenter', this._handleAutoplayPointerEnter);
            this._autoplayHoverZone.addEventListener('pointerout', this._handleAutoplayPointerOut);
        } else {
            this._autoplayHoverVideo = this._video;
            this._autoplayHoverVideo.addEventListener('pointerenter', this._handleAutoplayPointerEnter);
            this._autoplayHoverVideo.addEventListener('pointerout', this._handleAutoplayPointerOut);
        }
        this.addEventListener('pointerenter', this._handleAutoplayPointerEnter);
        this.addEventListener('pointerout', this._handleAutoplayPointerOut);
    }

    _detachAutoplayHover() {
        if (this._autoplayHoverZone) {
            this._autoplayHoverZone.removeEventListener('pointerenter', this._handleAutoplayPointerEnter);
            this._autoplayHoverZone.removeEventListener('pointerout', this._handleAutoplayPointerOut);
            this._autoplayHoverZone = null;
        }
        if (this._autoplayHoverVideo) {
            this._autoplayHoverVideo.removeEventListener('pointerenter', this._handleAutoplayPointerEnter);
            this._autoplayHoverVideo.removeEventListener('pointerout', this._handleAutoplayPointerOut);
            this._autoplayHoverVideo = null;
        }
        this.removeEventListener('pointerenter', this._handleAutoplayPointerEnter);
        this.removeEventListener('pointerout', this._handleAutoplayPointerOut);
    }

    _isWithinAutoplayHoverGroup(el) {
        if (!el) return false;
        if (el === this || this.contains(el)) return true;
        if (this._video && (el === this._video || this._video.contains(el))) return true;
        const zone = this._autoplayHoverZone;
        return Boolean(zone && (el === zone || zone.contains(el)));
    }

    _handleAutoplayPointerEnter() {
        if (!this._video) return;
        this._video.play().catch(() => {
            // Ignore autoplay/gesture-related rejections.
        });
    }

    _handleAutoplayPointerOut(e) {
        if (this._isWithinAutoplayHoverGroup(e.relatedTarget)) return;
        if (!this._video) return;
        this._video.pause();
    }

    _syncUiState() {
        if (!this._video || !this._pauseButton || !this._playButton) return;

        const isPlaying = !this._video.paused && !this._video.ended;

        this.classList.toggle('playing', isPlaying);
        this._video.classList.toggle('playing', isPlaying);

        this._setButtonVisibility(this._pauseButton, isPlaying);
        this._setButtonVisibility(this._playButton, !isPlaying);
    }

    _setButtonVisibility(button, isVisible) {
        button.hidden = !isVisible;
        button.style.display = isVisible ? '' : 'none';
        button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        button.tabIndex = isVisible ? 0 : -1;
    }

    _ensureVideoId() {
        if (!this._video) return '';
        if (!this._video.id) {
            this._video.id = `video-toggle-target-${Math.random().toString(36).slice(2, 10)}`;
        }
        return this._video.id;
    }
}

if (!window.customElements.get('video-toggle')) {
    customElements.define('video-toggle', VideoToggle);
}
